import React, { useState } from "react";
import {
  buildMetaMaskDeepLink,
  connectWallet,
  detectWalletType,
  isMobileBrowser,
  isWalletAvailable,
  openMetaMaskDeepLink,
  SUPPORTED_WALLETS,
} from "../utils/walletConnect";
import { useTranslation } from "../localization";

function WalletModal({
  walletAddress,
  platformBalance,
  onChainBalance,
  onConnect,
  onDisconnect,
  onClose,
}) {
  const { t } = useTranslation();
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState("");

  const detectedWallet = detectWalletType();

  const formatAddress = (address) => {
    if (!address) return "";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Detect if user is on mobile
  const isMobile = isMobileBrowser();
  const isMetaMaskAvailable = isWalletAvailable(SUPPORTED_WALLETS.METAMASK);

  const handleWalletConnect = async (walletType) => {
    setConnecting(true);
    setError("");

    try {
      if (
        walletType === SUPPORTED_WALLETS.METAMASK &&
        !isMetaMaskAvailable &&
        isMobile
      ) {
        openMetaMaskDeepLink(window.location.href);
        return;
      }

      const { address, provider } = await connectWallet(walletType);
      await onConnect(address, provider, walletType);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {walletAddress ? t("modal.wallet") : t("modal.connectWallet")}
          </h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {walletAddress ? (
            <>
              <div className="wallet-info">
                <div className="info-row">
                  <span className="info-label">{t("modal.address")}</span>
                  <span className="info-value mono">
                    {formatAddress(walletAddress)}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">
                    {t("modal.platformBalance")}
                  </span>
                  <span className="info-value">
                    ${platformBalance.toFixed(2)} USDC
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">
                    {t("modal.walletBalance")}
                  </span>
                  <span className="info-value">
                    ${onChainBalance.toFixed(2)} USDC
                  </span>
                </div>
              </div>

              <button className="disconnect-button" onClick={onDisconnect}>
                {t("modal.disconnectWallet")}
              </button>
            </>
          ) : (
            <>
              {error && <div className="error-message">{error}</div>}

              <div className="wallet-options">
                <h3>{t("modal.selectProvider")}</h3>

                {/* MetaMask */}
                <button
                  className="wallet-option-button"
                  onClick={() =>
                    handleWalletConnect(SUPPORTED_WALLETS.METAMASK)
                  }
                  disabled={connecting || (!isMetaMaskAvailable && !isMobile)}
                >
                  <span className="wallet-icon">🦊</span>
                  <span>MetaMask</span>
                  {detectedWallet === SUPPORTED_WALLETS.METAMASK && (
                    <span className="detected-badge">
                      {t("modal.detected")}
                    </span>
                  )}
                </button>

                {/* Coinbase Wallet */}
                <button
                  className="wallet-option-button"
                  onClick={() =>
                    handleWalletConnect(SUPPORTED_WALLETS.COINBASE)
                  }
                  disabled={
                    connecting || !isWalletAvailable(SUPPORTED_WALLETS.COINBASE)
                  }
                >
                  <span className="wallet-icon">🔵</span>
                  <span>Coinbase Wallet</span>
                  {detectedWallet === SUPPORTED_WALLETS.COINBASE && (
                    <span className="detected-badge">
                      {t("modal.detected")}
                    </span>
                  )}
                </button>

                {/* WalletConnect */}
                <button
                  className="wallet-option-button"
                  onClick={() =>
                    handleWalletConnect(SUPPORTED_WALLETS.WALLETCONNECT)
                  }
                  disabled={connecting}
                >
                  <span className="wallet-icon">📱</span>
                  <span>WalletConnect</span>
                  <span className="wallet-subtitle">
                    {t("modal.walletCount")}
                  </span>
                </button>

                {/* Any injected wallet */}
                {detectedWallet &&
                  detectedWallet !== SUPPORTED_WALLETS.METAMASK &&
                  detectedWallet !== SUPPORTED_WALLETS.COINBASE && (
                    <button
                      className="wallet-option-button"
                      onClick={() =>
                        handleWalletConnect(SUPPORTED_WALLETS.INJECTED)
                      }
                      disabled={connecting}
                    >
                      <span className="wallet-icon">🔗</span>
                      <span>
                        {detectedWallet === "Base Wallet"
                          ? "Base Wallet"
                          : t("modal.browserWallet")}
                      </span>
                      <span className="detected-badge">
                        {t("modal.detected")}
                      </span>
                    </button>
                  )}

                {/* Generic Web3 wallet fallback */}
                {window.ethereum && !detectedWallet && (
                  <button
                    className="wallet-option-button"
                    onClick={() =>
                      handleWalletConnect(SUPPORTED_WALLETS.INJECTED)
                    }
                    disabled={connecting}
                  >
                    <span className="wallet-icon">🔗</span>
                    <span>{t("modal.browserWallet")}</span>
                  </button>
                )}

                {!window.ethereum && (
                  <p className="wallet-prompt">
                    {isMobile ? (
                      t("modal.noInjectedMobile")
                    ) : (
                      t("modal.noWalletDesktop")
                    )}
                  </p>
                )}

                {isMobile && !isMetaMaskAvailable && (
                  <p className="wallet-prompt" style={{ marginTop: "8px" }}>
                    {t("modal.openMetaMaskPrompt")}{" "}
                    <br />
                    <a
                      href={buildMetaMaskDeepLink(window.location.href)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("modal.openMetaMask")}
                    </a>
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default WalletModal;
